import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ColorPicker } from "@/components/online/ColorPicker";
import { FieldError, INPUT_CLASS, OnlineShell, Panel } from "@/components/online/OnlineShell";
import { createLobby, errorText, getLobby, saveOnline } from "@/lib/api";

function CreateLobby() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [color, setColor] = useState("red");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Pick a name");
    setBusy(true);
    setError("");
    try {
      const { code, ticket } = await createLobby(name.trim(), color);
      saveOnline({ code, ticket, game_id: null });
      navigate(`/lobby/${code}`);
    } catch (err) {
      setError(errorText(err, "Could not create a lobby"));
      setBusy(false);
    }
  };

  return (
    <Panel testId="create-lobby-panel">
      <form onSubmit={submit}>
        <div className="label-kicker text-[color:var(--ink-3)] mb-4">Create a lobby</div>
        <Input data-testid="create-lobby-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={24} className={INPUT_CLASS} />
        <div className="mt-5">
          <ColorPicker value={color} onChange={setColor} />
        </div>
        <FieldError testId="create-lobby-error">{error}</FieldError>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy} className="btn-tactile btn-tactile--primary" data-testid="create-lobby-btn">
            {busy ? "Creating…" : "Create lobby →"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

function JoinByCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length !== 4) return setError("Codes are 4 letters");
    setBusy(true);
    setError("");
    try {
      await getLobby(c);
      navigate(`/join/${c}`);
    } catch (err) {
      setError(err?.response?.status === 404 ? "Lobby not found" : errorText(err, "Could not reach the server"));
      setBusy(false);
    }
  };

  return (
    <Panel testId="join-lobby-panel">
      <form onSubmit={submit}>
        <div className="label-kicker text-[color:var(--ink-3)] mb-4">Join a lobby</div>
        <div className="flex gap-3">
          <Input
            data-testid="join-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^a-z]/gi, "").toUpperCase())}
            placeholder="CODE"
            maxLength={4}
            autoCapitalize="characters"
            className={`${INPUT_CLASS} font-coord tracking-[0.3em] uppercase`}
          />
          <button type="submit" disabled={busy} className="btn-tactile shrink-0" data-testid="join-code-btn">
            {busy ? "Checking…" : "Join"}
          </button>
        </div>
        <FieldError testId="join-code-error">{error}</FieldError>
      </form>
    </Panel>
  );
}

export default function OnlineScreen() {
  return (
    <OnlineShell testId="online-screen" kicker="Play Online" title="Play together, apart" intro="Each player uses their own device. Create a lobby and share its code, or enter a code you were given.">
      <div className="space-y-5">
        <CreateLobby />
        <JoinByCode />
      </div>
    </OnlineShell>
  );
}
